import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export interface EstateBranding {
  subdomain: string;
  estateName: string;
  primaryColor: string;
  loginLogoUrl: string | null;
  loginBgType: string;
  loginBgColor: string;
  loginBgGradient: string | null;
  loginBgImageUrl: string | null;
  slogan: string | null;
  welcomeMessage: string | null;
  showSloganOnLogin: boolean;
}

const STORAGE_KEY = 'staffEstateBranding';

export const useEstateStore = defineStore('estate', () => {
  const stored = localStorage.getItem(STORAGE_KEY);
  const initial: EstateBranding | null = stored ? JSON.parse(stored) : null;

  const branding = ref<EstateBranding | null>(initial);

  const estateName = computed(() => branding.value?.estateName ?? 'Estate Staff');
  const primaryColor = computed(() => branding.value?.primaryColor ?? '#0F766E');

  function setBranding(data: EstateBranding) {
    branding.value = data;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }

  async function refreshBranding() {
    const subdomain = branding.value?.subdomain;
    if (!subdomain) return;
    try {
      const { data } = await axios.get(`${API_BASE_URL}/customization/public/${subdomain}`, {
        headers: { 'X-Estate-Subdomain': subdomain },
      });
      setBranding({
        subdomain,
        estateName: data.estateName ?? branding.value!.estateName,
        primaryColor: data.primaryColor ?? branding.value!.primaryColor,
        loginLogoUrl: data.loginLogoUrl ?? null,
        loginBgType: data.loginBgType ?? branding.value!.loginBgType,
        loginBgColor: data.loginBgColor ?? branding.value!.loginBgColor,
        loginBgGradient: data.loginBgGradient ?? null,
        loginBgImageUrl: data.loginBgImageUrl ?? null,
        slogan: data.slogan ?? null,
        welcomeMessage: data.welcomeMessage ?? null,
        showSloganOnLogin: data.showSloganOnLogin ?? branding.value!.showSloganOnLogin,
      });
    } catch {
      // silently keep existing branding
    }
  }

  function clear() {
    branding.value = null;
    localStorage.removeItem(STORAGE_KEY);
  }

  return { branding, estateName, primaryColor, setBranding, refreshBranding, clear };
});
